import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Download, Linkedin, X } from 'lucide-react';
import ShareCard from './ShareCard';
import { TeamsLogo } from './BrandIcons';
import { Chip, Field, Loader } from './ui';
import { useAuth } from '../contexts/AuthContext';
import { gamificationService } from '../services/api';
import {
  SHARE_WIDTH, TEAMS_URL, canShareFile, defaultText, downloadBlob, fileName, linkedInUrl, renderPng,
} from '../utils/share';
import './RewardModal.css';
import './ShareModal.css';

const TARGETS = [
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, bg: '#0a66c2', fg: '#fff' },
  { id: 'teams', label: 'Teams', icon: TeamsLogo, bg: '#5b5fc7', fg: '#fff' },
  { id: 'download', label: 'Baixar imagem', icon: Download, bg: 'var(--lime)', fg: 'var(--navy-900)' },
  { id: 'copy', label: 'Copiar texto', icon: Copy, bg: 'var(--tint-navy)', fg: 'var(--navy)' },
];

const APP_NAME = { linkedin: 'LinkedIn', teams: 'Teams' };
const OPENED = {
  linkedin: 'Imagem baixada e texto copiado. No LinkedIn, cole o texto (Ctrl+V) e anexe a imagem.',
  teams: 'Imagem baixada e texto copiado. No Teams, escolha a conversa, cole o texto e anexe a imagem.',
};
const COPY_FAILED = 'Não foi possível copiar. Selecione o texto e copie manualmente.';

/** Medalha mais recente da página Conquistas. */
function latestMedal(page) {
  return (page?.achievements || [])
    .filter(a => a.unlocked)
    .sort((a, b) => String(b.unlockedAt).localeCompare(String(a.unlockedAt)))[0];
}

/** Dados do cartão "Progresso" a partir da página Conquistas. */
function progressOf(page) {
  if (!page) return null;
  const golds = page.phases.flatMap(p => p.topics).filter(t => t.tier === 'gold').sort((a, b) => b.pct - a.pct);
  return {
    level: page.level.level,
    pct: page.level.pct,
    xp: page.level.xp,
    medals: page.medalsUnlocked,
    medalsTotal: page.medalsTotal,
    tiers: page.masteryCounts,
    streak: page.streak || 0,
    goldTopic: golds[0]?.name,
  };
}

/**
 * Compartilhar progresso: imagem 1200×627 (progresso, nível ou medalha) e texto editável,
 * para LinkedIn, Teams, baixar ou copiar.
 *
 * @param kind  tipo inicial: progress | level | medal
 * @param level { level, xp } de uma subida recente; sem ele, vale o nível atual
 * @param medal medalha aberta a partir do modal de medalha; sem ela, a última conquistada
 * @param page  dados da página Conquistas, quando já carregados
 */
function ShareModal({ courseSlug, courseName, kind: initialKind = 'progress', level: levelProp, medal: medalProp, page: pageProp, onClose }) {
  const { user } = useAuth();
  const [kind, setKind] = useState(initialKind);
  const [page, setPage] = useState(pageProp || null);
  const [showName, setShowName] = useState(true);
  const [edited, setEdited] = useState({});
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(0.56);
  const previewRef = useRef(null);
  const cardRef = useRef(null);
  const cache = useRef({ key: null, blob: null });
  const toastTimer = useRef(null);

  useEffect(() => {
    if (pageProp || !courseSlug) return;
    gamificationService.getPage(courseSlug).then(setPage).catch(() => setPage(null));
  }, [courseSlug, pageProp]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // A prévia acompanha a largura do modal (no celular fica menor que 672px)
  useEffect(() => {
    const el = previewRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / SHARE_WIDTH));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const progress = useMemo(() => progressOf(page), [page]);
  const level = levelProp || (page && { level: page.level.level, xp: page.level.xp });
  const medal = medalProp || latestMedal(page);

  const kinds = [
    { id: 'progress', label: 'Progresso' },
    level && { id: 'level', label: `Nível ${level.level}` },
    medal && { id: 'medal', label: 'Medalha' },
  ].filter(Boolean);

  const ready = (kind === 'progress' && progress) || (kind === 'level' && level) || (kind === 'medal' && medal);
  const text = edited[kind] ?? (ready ? defaultText(kind, { courseName, progress, level, medal }) : '');
  const cacheKey = `${kind}:${showName}:${ready ? 'ok' : '-'}`;

  const getBlob = useCallback(async () => {
    if (cache.current.key === cacheKey && cache.current.blob) return cache.current.blob;
    const blob = await renderPng(cardRef.current);
    cache.current = { key: cacheKey, blob };
    return blob;
  }, [cacheKey]);

  // Gera a imagem com antecedência: o compartilhamento nativo do celular precisa do arquivo na hora do toque
  useEffect(() => {
    if (!ready) return undefined;
    const t = setTimeout(() => { getBlob().catch(() => {}); }, 400);
    return () => clearTimeout(t);
  }, [ready, getBlob]);

  const say = (message, tone = 'ok') => {
    setToast({ message, tone });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  };

  const go = async (target) => {
    if (busy || !ready) return;
    if (target === 'copy') {
      if (await copyText()) say('Texto copiado.');
      else say(COPY_FAILED, 'warn');
      return;
    }

    // No celular, a folha de compartilhamento nativa anexa a imagem
    const cached = cache.current.key === cacheKey ? cache.current.blob : null;
    if (target !== 'download' && cached) {
      const file = new File([cached], fileName(kind), { type: 'image/png' });
      if (canShareFile(file)) {
        try {
          await navigator.share({ files: [file], text });
        } catch (e) {
          if (e?.name !== 'AbortError') say('Não foi possível abrir o compartilhamento. Baixe a imagem e copie o texto.', 'warn');
        }
        return;
      }
    }

    // A aba abre já no clique, senão o navegador a bloqueia; o endereço entra depois
    const tab = target === 'download' ? null : window.open('', '_blank');
    setBusy(true);
    try {
      const copied = target === 'download' ? true : await copyText();
      const blob = await getBlob();
      downloadBlob(blob, fileName(kind));
      if (target === 'download') {
        say(`${fileName(kind)} salvo (1200×627).`);
        return;
      }
      const url = target === 'linkedin' ? linkedInUrl(text) : TEAMS_URL;
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      }
      if (!copied) say(COPY_FAILED, 'warn');
      else if (!tab) say(`Imagem baixada e texto copiado, mas o navegador bloqueou a nova aba. Abra o ${APP_NAME[target]} e cole o texto.`, 'warn');
      else say(OPENED[target]);
    } catch {
      tab?.close();
      say('Não foi possível gerar a imagem. Tente de novo.', 'warn');
    } finally {
      setBusy(false);
    }
  };

  const cardProps = {
    kind,
    courseName,
    userName: user?.name,
    showName,
    progress,
    level,
    medal,
  };

  return (
    <div className="ss-modal shm" onClick={onClose}>
      <div
        className="ss-modal__panel shm__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Compartilhar progresso"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ss-modal__head">
          <h2>Compartilhar progresso</h2>
          <button type="button" className="rw-close shm__close" onClick={onClose} aria-label="Fechar">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="ss-modal__body">
          <div className="shm__kinds" role="group" aria-label="O que compartilhar">
            {kinds.map(k => (
              <Chip key={k.id} active={kind === k.id} onClick={() => { setKind(k.id); setToast(null); }}>{k.label}</Chip>
            ))}
          </div>

          <div className="shm__preview" ref={previewRef}>
            {ready ? (
              <div className="shm__scale" style={{ transform: `scale(${scale})` }}>
                <ShareCard {...cardProps} />
              </div>
            ) : (
              <Loader label="Carregando seu progresso..." />
            )}
          </div>

          <label className="shm__check">
            <input type="checkbox" checked={showName} onChange={() => setShowName(v => !v)} />
            <span>Mostrar meu nome na imagem</span>
          </label>

          <Field label="Texto do post">
            <textarea
              rows={3}
              value={text}
              onChange={(e) => {
                const v = e.target.value;
                setEdited(s => ({ ...s, [kind]: v }));
              }}
            />
          </Field>

          <div className="shm__targets">
            {TARGETS.map(t => (
              <button key={t.id} type="button" className="shm__target" onClick={() => go(t.id)} disabled={busy || !ready}>
                <span className="shm__targeticon" style={{ background: t.bg, color: t.fg }}>
                  <t.icon size={20} aria-hidden="true" />
                </span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {toast && (
          <div className={`shm__toast ${toast.tone === 'warn' ? 'is-warn' : ''}`} role="status">
            <span className="shm__toasticon" aria-hidden="true">{toast.tone === 'warn' ? '!' : '✓'}</span>
            <span>{toast.message}</span>
          </div>
        )}
      </div>

      {/* Cópia em tamanho real, fora da tela, de onde sai o PNG */}
      {ready && (
        <div className="shm__offscreen" aria-hidden="true">
          <ShareCard ref={cardRef} {...cardProps} />
        </div>
      )}
    </div>
  );
}

export default ShareModal;
